import React from 'react';
const { useState, useMemo } = React;
import { cardSt, labelSt, mutedSt, badgeSt, inputSt, fmtDate } from '../diagnostic/shared.jsx';
import { PageHeader } from '../ui/States.jsx';
import { teamOverview, teamCandidates, assignmentHistoryOf } from '../../teamView.js';
import { linkedIdsOf, ASSIGNMENT_HISTORY_FIELD } from '../../commercialScope.js';
import { teamCall } from '../../teamClient.js';
import { loadErrorOf } from '../../dataLoad.js';

// «Equipo» (only the administration of the central base): who works each company, what each person has pending and
// the assignments to resolve. The pending items are the same queue as Seguimiento (nothing is copied) and stay out of
// Ale's own Hoy list; the full history of each company stays where it was. «Responsable» is the only way to assign or
// reassign (team.mts › assign): with confirmation and a line in Assignment History; nothing is chosen automatically.

const smallBtn = { fontSize: 11, padding: '2px 8px' };
const KIND = { inbound: 'respuesta', gmail_sent: 'correo enviado', confirmed: 'contacto confirmado', logged: 'registrado a mano', opened: 'canal abierto (no cuenta)', ignored: 'borrador' };
const HOW = { assigned: 'asignó', reassigned: 'reasignó', unassigned: 'quitó el responsable', created: 'creó la empresa y quedó a cargo de' };
const openAccount = (accountId) => window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'accounts', accountId } }));

/** Accounts › Assignment History, newest first (who, when, from whom, to whom). */
export function AssignmentHistory({ account }) {
  const events = assignmentHistoryOf(account?.fields?.[ASSIGNMENT_HISTORY_FIELD]);
  if (!events.length) return <div style={mutedSt}>Sin cambios de responsable registrados.</div>;
  return (
    <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12, display: 'grid', gap: 2 }}>
      {events.map((e, i) => <li key={i}>{e.raw ? e.raw : <>{e.at ? fmtDate(e.at, true) : ''} · {e.by || 'alguien'} {HOW[e.how] || 'cambió'}{e.from?.length ? ` de ${e.from.map(x => x.name || 'sin nombre').join(' y ')}` : ''}{e.to ? ` ${e.how === 'created' ? '' : 'a '}${e.to.name || 'sin nombre'}` : ''}</>}</li>)}
    </ul>
  );
}

/**
 * Responsable of one company: one person of the team (a Users record with a unique email) or nobody. The screen sends
 * the responsible it shows, so a change made meanwhile is never overwritten. Several responsibles are never resolved
 * by picking one: Ale chooses.
 */
export function ResponsiblePicker({ account, users, onUpdateRecord, compact = false }) {
  const people = useMemo(() => teamCandidates(users || []), [users]);
  const current = linkedIdsOf(account, 'BDR');
  const [choice, setChoice] = useState(current.length === 1 ? current[0] : '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const nameOf = (id) => people.find(p => p.id === id)?.name || 'ficha que ya no existe';
  const changed = current.length !== 1 ? choice !== '' || current.length > 1 : choice !== current[0];
  const save = async () => {
    const from = current.length ? current.map(nameOf).join(' y ') : 'nadie';
    const to = choice ? nameOf(choice) : 'nadie';
    if (!window.confirm(`¿Cambiar el responsable de «${account.fields?.['Account Name'] || 'esta empresa'}» de ${from} a ${to}?\n\nQueda en su historial. ${current.length ? 'Quien la tenía deja de verla y de poder trabajarla; lo que registró se conserva.' : ''}`)) return;
    setBusy(true); setMsg('');
    try {
      const r = await teamCall({ action: 'assign', accountId: account.id, userId: choice, expected: current, confirm: true });
      if (!r.unchanged) onUpdateRecord?.('accounts', account.id, r.account.fields);
      setMsg(r.unchanged ? 'Sin cambios.' : 'Listo: quedó en el historial.');
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  };
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      {current.length > 1 && <div style={{ fontSize: 12, color: '#9A6212' }}>Tiene {current.length} responsables ({current.map(nameOf).join(', ')}): nadie del equipo la ve hasta que elijas uno.</div>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {!compact && <span style={labelSt}>Responsable</span>}
        <select style={{ ...inputSt, width: 'auto', fontSize: 12 }} value={choice} disabled={busy} onChange={e => setChoice(e.target.value)} aria-label="Responsable">
          <option value="">Sin responsable</option>
          {people.map(p => <option key={p.id} value={p.id} disabled={!p.identifiable}>{p.name}{p.identifiable ? '' : p.problem === 'ambiguous' ? ' (email repetido en Users)' : ' (sin email en Users)'}</option>)}
        </select>
        <button type="button" className="action-btn btn-primary" style={smallBtn} disabled={busy || !changed} onClick={save}>{busy ? 'Guardando…' : 'Cambiar'}</button>
        <button type="button" className="action-btn" style={smallBtn} aria-expanded={showHistory} onClick={() => setShowHistory(v => !v)}>Historial</button>
      </div>
      {msg && <div role="status" style={{ fontSize: 12, color: /Listo|Sin cambios/.test(msg) ? 'var(--globant-green)' : '#9A6212' }}>{msg}</div>}
      {showHistory && <AssignmentHistory account={account} />}
    </div>
  );
}

function AccountRow({ a, account, users, onUpdateRecord }) {
  const [open, setOpen] = useState(false);
  const pending = a.replies.length + a.followups.length;
  return (
    <div style={{ padding: '6px 0', borderTop: '1px solid var(--globant-border)', fontSize: 13 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <b style={{ flex: '1 1 180px', minWidth: 0 }}>{a.name}</b>
        <span style={mutedSt}>{a.contacts} contacto{a.contacts === 1 ? '' : 's'}</span>
        {a.replies.length > 0 && <span style={badgeSt(false)}>↩ {a.replies.length} por responder</span>}
        {a.followups.length > 0 && <span style={badgeSt(false)}>✉ {a.followups.length} seguimiento{a.followups.length === 1 ? '' : 's'}</span>}
        {!pending && <span style={mutedSt}>sin pendientes</span>}
        <span style={mutedSt}>{a.last ? `última actividad ${a.last.date ? fmtDate(a.last.date) : ''}${a.last.by ? ` · ${a.last.by}` : ''} · ${KIND[a.last.kind] || a.last.kind}` : 'sin actividad'}</span>
        <button type="button" className="action-btn" style={smallBtn} onClick={() => openAccount(a.id)}>Abrir</button>
        <button type="button" className="action-btn" style={smallBtn} aria-expanded={open} onClick={() => setOpen(o => !o)}>{open ? 'Cerrar' : 'Ver'}</button>
      </div>
      {open && (
        <div style={{ margin: '6px 0 2px 12px', display: 'grid', gap: 6 }}>
          {a.replies.length > 0 && <div style={{ fontSize: 12 }}><b>Por responder:</b> {a.replies.map(r => `${r.name || 'contacto'}${r.daysSince != null ? ` (hace ${r.daysSince} d)` : ''}`).join(' · ')}</div>}
          {a.followups.length > 0 && <div style={{ fontSize: 12 }}><b>Seguimientos:</b> {a.followups.map(f => `${f.name || 'contacto'}${f.tag === 'urgent' ? ' · urgente' : ''}${f.daysSince != null ? ` (sin contacto hace ${f.daysSince} d)` : ''}`).join(' · ')}</div>}
          {account && <ResponsiblePicker account={account} users={users} onUpdateRecord={onUpdateRecord} />}
        </div>
      )}
    </div>
  );
}

export default function TeamView({ data, onUpdateRecord }) {
  const { accounts = [], users = [], stakeholders = [], outreach = [], campaigns = [] } = data || {};
  const v = useMemo(() => teamOverview({ accounts, users, stakeholders, outreach, campaigns }), [accounts, users, stakeholders, outreach, campaigns]);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const failed = loadErrorOf(data, ['accounts', 'users', 'stakeholders', 'outreach']);
  const accById = useMemo(() => new Map(accounts.map(a => [a.id, a])), [accounts]);
  const STATE = { multiple: 'tiene varios responsables', legacy: 'asignada solo por nombre (BDR Owner), sin responsable en BDR', missing_user: 'su responsable ya no está en Users', unidentifiable: 'su responsable no tiene un email único en Users (no puede entrar como colaborador)' };
  return (
    <div className="page-content" style={{ padding: 20, maxWidth: 1000 }}>
      <PageHeader area="Negocio" title="Equipo" subtitle="Quién trabaja cada empresa, qué tiene pendiente y qué asignaciones tenés que resolver. Solo vos lo ves." />
      {failed && <div role="alert" style={{ ...cardSt, color: '#9A6212', fontSize: 13 }}>No se pudo cargar todo ({failed}): esta vista puede estar incompleta, no vacía. Usá «Reintentar» arriba.</div>}
      {v.toResolve.length > 0 && (
        <section aria-label="Asignaciones por resolver" style={{ ...cardSt, borderLeft: '3px solid rgba(245,158,11,0.6)' }}>
          <div style={labelSt}>Por resolver ({v.toResolve.length})</div>
          <div style={{ ...mutedSt, marginBottom: 4 }}>Oike no elige ni sobrescribe: mientras no lo resuelvas, nadie del equipo trabaja estas empresas con la regla nueva.</div>
          {v.toResolve.map(r => (
            <div key={`${r.state}-${r.id}`} style={{ padding: '6px 0', borderTop: '1px solid var(--globant-border)', fontSize: 13, display: 'grid', gap: 4 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <b>{r.name}</b><span style={mutedSt}>{STATE[r.state]}{r.who?.length ? `: ${r.who.join(', ')}` : ''}</span>
                <button type="button" className="action-btn" style={smallBtn} onClick={() => openAccount(r.id)}>Abrir</button>
              </div>
              {accById.get(r.id) && <ResponsiblePicker account={accById.get(r.id)} users={users} onUpdateRecord={onUpdateRecord} compact />}
            </div>
          ))}
        </section>
      )}
      {!v.members.length && <div style={{ ...cardSt, ...mutedSt }}>Todavía no hay empresas asignadas a nadie del equipo.</div>}
      {v.members.map(m => (
        <section key={m.user.id} aria-label={`Empresas de ${m.user.name}`} style={cardSt}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 15, flex: '1 1 200px' }}>{m.user.name}</span>
            {m.user.email && <span style={mutedSt}>{m.user.email}</span>}
            {!m.user.identifiable && <span style={{ ...badgeSt(false), color: '#9A6212' }}>{m.user.problem === 'ambiguous' ? 'email repetido en Users' : 'sin email en Users'}</span>}
            <span style={mutedSt}>{m.totals.accounts} empresa{m.totals.accounts === 1 ? '' : 's'} · {m.totals.replies} por responder · {m.totals.followups} seguimiento{m.totals.followups === 1 ? '' : 's'}</span>
          </div>
          {m.accounts.map(a => <AccountRow key={a.id} a={a} account={accById.get(a.id)} users={users} onUpdateRecord={onUpdateRecord} />)}
        </section>
      ))}
      <section aria-label="Sin responsable" style={cardSt}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ ...labelSt, flex: '1 1 auto' }}>Sin responsable ({v.unassigned.length})</span>
          <button type="button" className="action-btn" style={smallBtn} aria-expanded={showUnassigned} onClick={() => setShowUnassigned(s => !s)}>{showUnassigned ? 'Ocultar' : 'Ver'}</button>
        </div>
        {showUnassigned && v.unassigned.map(a => (
          <div key={a.id} style={{ padding: '6px 0', borderTop: '1px solid var(--globant-border)', fontSize: 13, display: 'grid', gap: 4 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}><b style={{ flex: 1 }}>{a.name}</b><button type="button" className="action-btn" style={smallBtn} onClick={() => openAccount(a.id)}>Abrir</button></div>
            {accById.get(a.id) && <ResponsiblePicker account={accById.get(a.id)} users={users} onUpdateRecord={onUpdateRecord} compact />}
          </div>
        ))}
      </section>
      <div style={{ ...mutedSt, marginTop: 8 }}>Los pendientes de empresas asignadas a otra persona no se mezclan con tu Hoy: están acá. Su historial completo sigue en cada empresa y en Seguimiento. Una asignación nunca se registra como contacto comercial.</div>
    </div>
  );
}
